<div class="upgrade-container">
    <!-- Left column: main content -->
    <div class="upgrade-main">
        <!-- Step 1: Connect Hardware -->
        <div id="step1Section" class="guide-step-card" [class.step-completed]="currentGuideStep > 1" #step1Section>
            <div class="step-card-header">
                <div class="step-badge" [class.badge-completed]="currentGuideStep > 1">
                    <span *ngIf="currentGuideStep > 1" class="check-icon">✓</span>
                    <span *ngIf="currentGuideStep <= 1">1</span>
                </div>
                <div class="step-header-text">
                    <h4>Step 1: Connect Hardware</h4>
                    <p class="step-instruction">Power on the TetraGraph and connect it to your PC with a USB cable</p>
                </div>
                <div class="step-action">
                    <button
                        class="guide-btn"
                        [class.btn-completed]="currentGuideStep > 1"
                        [disabled]="currentGuideStep > 1"
                        (click)="onStep1Done()"
                    >
                        <span *ngIf="currentGuideStep > 1">✓ </span>Next
                    </button>
                </div>
            </div>
        </div>

        <!-- Step 2: Select Region & Connect -->
        <div
            id="step2Section"
            class="guide-step-card"
            [class.step-completed]="isConnected"
            *ngIf="currentGuideStep >= 2"
            #step2Section
        >
            <div class="step-card-header">
                <div class="step-badge" [class.badge-completed]="isConnected">
                    <span *ngIf="isConnected" class="check-icon">✓</span>
                    <span *ngIf="!isConnected">2</span>
                </div>
                <div class="step-header-text">
                    <h4>Step 2: Connect to Application</h4>
                    <p class="step-instruction">
                        Select your region and click Connect. Select STM 32 USB Device in the pop-up window
                    </p>
                </div>
            </div>

            <div class="step-card-body">
                <!-- Browser Support Check -->
                <div
                    *ngIf="!isSupported"
                    class="support-status"
                    [ngClass]="{ supported: isSupported, 'not-supported': !isSupported }"
                >
                    <div class="status-icon">
                        {{ isSupported ? '✓' : '✗' }}
                    </div>
                    <div class="status-text">
                        <strong>Web Serial API:</strong>
                        {{ isSupported ? 'Supported' : 'Not Supported' }}
                    </div>
                    <div class="status-description" *ngIf="!isSupported">
                        This browser doesn't support the Web Serial API. Please use Chrome or Edge.
                    </div>
                </div>

                <!-- Connection Controls -->
                <div class="connection-controls">
                    <div class="connection-status">
                        <div
                            class="status-indicator"
                            [ngClass]="{ connected: isConnected, disconnected: !isConnected }"
                        >
                            {{ isConnected ? '🟢' : '🔴' }}
                        </div>
                        <span class="status-label">
                            {{ isConnected ? 'Connected' : 'Disconnected' }}
                        </span>
                    </div>

                    <!-- Controls Row: Region Selection + Buttons -->
                    <div class="controls-row">
                        <!-- Region Selection (visible only when not connected) -->
                        <div class="region-selection" *ngIf="!isConnected && (regions$ | async)?.length > 0">
                            <app-dropdown
                                [items]="regions$ | async"
                                [(ngModel)]="selectedRegionId"
                                placeholder="{{ 'FEATURES.DEVICE.SELECT_REGION' | translate }}"
                            />
                        </div>

                        <!-- Region Display (visible only when connected) -->
                        <div class="region-display" *ngIf="isConnected && selectedRegionId">
                            <strong>Region:</strong> {{ getRegionName(selectedRegionId) }}
                        </div>

                        <div class="control-buttons">
                            <button
                                class="connect-btn"
                                [disabled]="!isSupported || isConnecting || isConnected || !selectedRegionId"
                                (click)="onConnect()"
                            >
                                {{ isConnecting ? 'Connecting...' : 'Connect' }}
                            </button>
                            <button *ngIf="isConnected" class="disconnect-btn" (click)="onDisconnect()">
                                Disconnect
                            </button>
                        </div>
                    </div>

                    <!-- Connection Error Message -->
                    <div *ngIf="connectionError" class="error-message">
                        <strong>Error:</strong> {{ connectionError }}
                    </div>

                    <!-- Device Info -->
                    <div *ngIf="isConnected && currentDevice" class="device-info">
                        <strong>Connected Device:</strong> {{ deviceDisplayName }}
                    </div>

                    <!-- Retrieving Version -->
                    <div *ngIf="isRetrievingVersion" class="device-info">
                        <strong>Retrieving firmware version...</strong>
                    </div>

                    <!-- Version Display with Expand/Collapse -->
                    <div *ngIf="isConnected && deviceVersion" class="device-info">
                        <div class="version-info-header">
                            <strong>Software Version: {{ softwareVersion }}</strong>
                            <button
                                class="expand-btn"
                                (click)="showDeviceVersionDetails = !showDeviceVersionDetails"
                                [attr.aria-expanded]="showDeviceVersionDetails"
                            >
                                <span class="arrow" [ngClass]="{ expanded: showDeviceVersionDetails }">▼</span>
                            </button>
                        </div>
                        <pre *ngIf="showDeviceVersionDetails" class="version-text">{{ deviceVersion }}</pre>
                    </div>

                    <!-- Version Retrieval Error -->
                    <div *ngIf="isConnected && versionError" class="error-message">
                        <strong>Version Error:</strong> {{ versionError }}
                    </div>

                    <!-- Retrieving Serial Number -->
                    <div *ngIf="isRetrievingSerialNumber" class="device-info">
                        <strong>Retrieving device serial number...</strong>
                    </div>

                    <!-- Serial Number Display -->
                    <div *ngIf="isConnected && deviceSerialNumber" class="device-info">
                        <strong>Device Serial Number:</strong> {{ deviceSerialNumber }}
                    </div>

                    <!-- Calibration Status -->
                    <ng-container *ngIf="isConnected && calibrationVerification as calibration">
                        <div [ngSwitch]="calibration.deviceCalibrationStatusId" class="calibration-status">
                            <div
                                *ngSwitchCase="calibrationStatuses.BaselineRecorded"
                                class="version-status-banner info-banner"
                            >
                                <div class="status-banner-content">
                                    <span class="status-banner-icon">ℹ</span>
                                    <div class="status-banner-text">
                                        <strong>{{
                                            'FEATURES.DEVICE.CALIBRATION_SECTION.BASELINE_RECORDED_TITLE' | translate
                                        }}</strong>
                                        {{ 'FEATURES.DEVICE.CALIBRATION_SECTION.BASELINE_RECORDED_BODY' | translate }}
                                    </div>
                                </div>
                            </div>

                            <div
                                *ngSwitchCase="calibrationStatuses.NeedsMoreTime"
                                class="version-status-banner info-banner"
                            >
                                <div class="status-banner-content">
                                    <span class="status-banner-icon">ℹ</span>
                                    <div class="status-banner-text">
                                        <strong>{{
                                            'FEATURES.DEVICE.CALIBRATION_SECTION.NEEDS_MORE_TIME_TITLE' | translate
                                        }}</strong>
                                        {{ 'FEATURES.DEVICE.CALIBRATION_SECTION.NEEDS_MORE_TIME_BODY' | translate }}
                                    </div>
                                </div>
                            </div>

                            <div
                                *ngSwitchCase="calibrationStatuses.VerifiedAccurate"
                                class="version-status-banner success-banner"
                            >
                                <div class="status-banner-content">
                                    <span class="status-banner-icon">✓</span>
                                    <div class="status-banner-text">
                                        <strong>{{
                                            'FEATURES.DEVICE.CALIBRATION_SECTION.VERIFIED_ACCURATE_TITLE' | translate
                                        }}</strong>
                                        {{ 'FEATURES.DEVICE.CALIBRATION_SECTION.VERIFIED_ACCURATE_BODY' | translate }}
                                    </div>
                                </div>
                            </div>

                            <div
                                *ngSwitchCase="calibrationStatuses.DriftTooHigh"
                                class="version-status-banner warning-banner"
                            >
                                <div class="status-banner-content">
                                    <span class="status-banner-icon">⚠</span>
                                    <div class="status-banner-text">
                                        <strong>{{
                                            'FEATURES.DEVICE.CALIBRATION_SECTION.DRIFT_TOO_HIGH_TITLE' | translate
                                        }}</strong>
                                        {{
                                            'FEATURES.DEVICE.CLOCK_SYNC.DRIFT_TOO_HIGH'
                                                | translate: { serialNumber: deviceSerialNumber }
                                        }}
                                    </div>
                                </div>
                            </div>

                            <div
                                *ngSwitchCase="calibrationStatuses.EligibleForCalibration"
                                class="version-status-banner info-banner"
                            >
                                <div class="status-banner-content">
                                    <span class="status-banner-icon">ℹ</span>
                                    <div class="status-banner-text">
                                        <strong>{{
                                            'FEATURES.DEVICE.CALIBRATION_SECTION.ELIGIBLE_TITLE' | translate
                                        }}</strong>
                                        {{
                                            'FEATURES.DEVICE.CALIBRATION_SECTION.ELIGIBLE_BODY'
                                                | translate: { ppm: calibration.driftPPM?.toFixed(2) ?? '-' }
                                        }}
                                    </div>
                                </div>
                                <div class="calibration-action">
                                    <button
                                        type="button"
                                        class="calibrate-btn"
                                        [disabled]="isVerifying || (clockSyncService.isSynchronizing$ | async)"
                                        (click)="onCalibrate()"
                                    >
                                        {{ 'FEATURES.DEVICE.CLOCK_SYNC.CALIBRATE' | translate }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </ng-container>

                    <!-- Checking Available Versions -->
                    <div *ngIf="isConnected && isCheckingVersions" class="device-info">
                        <strong>Checking available firmware versions...</strong>
                    </div>

                    <!-- Version Status Banner: Latest in Region -->
                    <div
                        *ngIf="isConnected && versionComparisonStatus === versionComparisonStatuses.LatestInRegion"
                        class="version-status-banner info-banner"
                    >
                        <div class="status-banner-content">
                            <span class="status-banner-icon">ℹ</span>
                            <div class="status-banner-text">
                                <strong
                                    >The installed software version is the latest version available in your
                                    region!</strong
                                >
                            </div>
                        </div>
                        <div *ngIf="lowerVersionsAvailable.length > 0" class="downgrade-action">
                            <button class="downgrade-btn" (click)="onStartDowngrade()">
                                Install an earlier version
                            </button>
                        </div>
                    </div>

                    <!-- Version Status Banner: Latest Globally -->
                    <div
                        *ngIf="isConnected && versionComparisonStatus === versionComparisonStatuses.LatestGlobally"
                        class="version-status-banner success-banner"
                    >
                        <div class="status-banner-content">
                            <span class="status-banner-icon">✓</span>
                            <div class="status-banner-text">
                                <strong>Your device already has the latest software version!</strong>
                            </div>
                        </div>
                        <div *ngIf="lowerVersionsAvailable.length > 0" class="downgrade-action">
                            <button class="downgrade-btn" (click)="onStartDowngrade()">
                                Install an earlier version
                            </button>
                        </div>
                    </div>

                    <!-- Version Status Banner: No packages for region -->
                    <div
                        *ngIf="isConnected && versionComparisonStatus === versionComparisonStatuses.NoPackages"
                        class="version-status-banner warning-banner"
                    >
                        <div class="status-banner-content">
                            <span class="status-banner-icon">⚠️</span>
                            <div class="status-banner-text">
                                <strong>No software packages are currently available for this region.</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 3: Enable Receiver Mode on TetraGraph -->
        <div
            id="step3Section"
            class="guide-step-card"
            [class.step-completed]="currentGuideStep > 3"
            *ngIf="currentGuideStep >= 3"
            #step3Section
        >
            <div class="step-card-header">
                <div class="step-badge" [class.badge-completed]="currentGuideStep > 3">
                    <span *ngIf="currentGuideStep > 3" class="check-icon">✓</span>
                    <span *ngIf="currentGuideStep <= 3">3</span>
                </div>
                <div class="step-header-text">
                    <h4>Step 3: Enable Receiver Mode</h4>
                    <p class="step-instruction">
                        On TetraGraph, navigate to <strong>Settings</strong> &rarr; <strong>Device</strong> &rarr;
                        <strong>Service</strong> &rarr; Enter password <strong>736946</strong> &rarr; Click on check
                        mark &rarr; <strong>Start update session</strong> &rarr; <strong>Start receiver</strong>
                    </p>
                </div>
                <div class="step-action">
                    <button
                        class="guide-btn"
                        [class.btn-completed]="currentGuideStep > 3"
                        [disabled]="currentGuideStep > 3"
                        (click)="onStep3Done()"
                    >
                        <span *ngIf="currentGuideStep > 3">✓ </span>Next
                    </button>
                </div>
            </div>
        </div>

        <!-- Step 4: Select Software Package & Upgrade -->
        <div
            id="step4Section"
            class="guide-step-card"
            [class.step-completed]="isUploadComplete()"
            *ngIf="
                currentGuideStep >= 4 &&
                (isConnected || uploadProgress.isSending || uploadProgress.overallBytesTransferred > 0)
            "
            #step4Section
        >
            <div class="step-card-header">
                <div class="step-badge" [class.badge-completed]="isUploadComplete()">
                    <span *ngIf="isUploadComplete()" class="check-icon">✓</span>
                    <span *ngIf="!isUploadComplete()">4</span>
                </div>
                <div class="step-header-text">
                    <h4>
                        {{
                            isDowngradeMode
                                ? 'Step 4: Select Earlier Software Package & Install'
                                : 'Step 4: Select Software Package & Upgrade'
                        }}
                    </h4>
                    <p class="step-instruction">
                        Select the target software package below to view details and start the upgrade.
                    </p>
                </div>
            </div>

            <div class="step-card-body">
                <!-- Checking Available Versions -->
                <div *ngIf="isCheckingVersions" class="device-info">
                    <strong>Checking available firmware versions...</strong>
                </div>

                <!-- Version Check Error -->
                <div *ngIf="checkVersionsError" class="error-message">
                    <strong>Version Check Error:</strong> {{ checkVersionsError }}
                </div>

                <!-- Available Versions Section -->
                <div class="available-versions-section" *ngIf="displayedVersions.length > 0">
                    <h3>{{ isDowngradeMode ? 'Select Earlier Software Version' : 'Select Software Version' }}</h3>

                    <!-- Version List -->
                    <div class="versions-list">
                        <!-- Latest / First Version -->
                        <div
                            class="version-item"
                            *ngIf="displayedVersions.length > 0"
                            [ngClass]="{ selected: selectedVersion?.versionId === displayedVersions[0].versionId }"
                            (click)="onSelectVersion(displayedVersions[0])"
                            (keyup.enter)="onSelectVersion(displayedVersions[0])"
                            (keyup.space)="onSelectVersion(displayedVersions[0])"
                            tabindex="0"
                            role="button"
                        >
                            <div class="version-header">
                                <div class="version-name">
                                    <div class="version-title">
                                        <strong>{{ displayedVersions[0].name }}</strong>
                                        <span class="version-number">(v{{ displayedVersions[0].version }})</span>
                                    </div>
                                    <span *ngIf="!isDowngradeMode" class="latest-badge">Latest</span>
                                    <span *ngIf="isDowngradeMode" class="downgrade-badge">Earlier Version</span>
                                </div>
                                <div class="version-date">
                                    {{ displayedVersions[0].releaseDate | date: 'yyyy-MM-dd HH:mm' }}
                                </div>
                            </div>
                            <div class="version-notes">
                                {{ displayedVersions[0].description }}
                            </div>
                        </div>

                        <!-- Earlier Versions (shown when toggled) -->
                        <div *ngIf="showEarlierVersions && displayedVersions.length > 1" class="earlier-versions-group">
                            <div
                                class="version-item"
                                *ngFor="let version of displayedVersions.slice(1); let i = index"
                                [ngClass]="{ selected: selectedVersion?.versionId === version.versionId }"
                                (click)="onSelectVersion(version)"
                                (keyup.enter)="onSelectVersion(version)"
                                (keyup.space)="onSelectVersion(version)"
                                tabindex="0"
                                role="button"
                            >
                                <div class="version-header">
                                    <div class="version-name">
                                        <strong>{{ version.name }}</strong>
                                        <span class="version-number">(v{{ version.version }})</span>
                                    </div>
                                    <div class="version-date">{{ version.releaseDate | date: 'yyyy-MM-dd HH:mm' }}</div>
                                </div>
                                <div class="version-notes">
                                    {{ version.description }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Show/Hide Earlier Versions Button -->
                    <div class="earlier-versions-toggle" *ngIf="displayedVersions.length > 1">
                        <button class="toggle-btn" (click)="showEarlierVersions = !showEarlierVersions">
                            {{ showEarlierVersions ? '▲ Hide Earlier Versions' : '▼ Show Earlier Versions' }}
                            <span class="version-count">({{ displayedVersions.length - 1 }})</span>
                        </button>
                    </div>
                </div>

                <!-- Selected Version Release Info -->
                <div
                    id="selectedVersionSection"
                    class="selected-version-section"
                    *ngIf="selectedVersion"
                    #selectedVersionSection
                >
                    <h3>Selected Version Details</h3>
                    <div class="release-info-card">
                        <div class="release-header">
                            <div class="release-title">
                                <strong class="release-name">{{ selectedVersion.name }}</strong>
                                <span class="release-version">(v{{ selectedVersion.version }})</span>
                            </div>
                            <div class="release-date">
                                Released: {{ selectedVersion.releaseDate | date: 'yyyy-MM-dd HH:mm' }}
                            </div>
                        </div>
                        <div class="release-description">
                            <strong>Description:</strong>
                            <p>{{ selectedVersion.description }}</p>
                        </div>
                        <div class="release-notes-full">
                            <strong>Release Notes:</strong>
                            <p>{{ selectedVersion.releaseNotes }}</p>
                        </div>
                        <div class="upgrade-actions">
                            <button
                                class="upgrade-btn"
                                (click)="onStartUpgrade()"
                                [disabled]="
                                    !isConnected ||
                                    isPreparingUpgrade ||
                                    uploadProgress.isSending ||
                                    (clockSyncService.isSynchronizing$ | async)
                                "
                            >
                                {{
                                    isPreparingUpgrade
                                        ? 'Preparing...'
                                        : isDowngradeMode
                                          ? 'Install Earlier Version'
                                          : 'Start Upgrade'
                                }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Upload Progress (shown even after disconnect if upload was initiated) -->
                <div
                    id="uploadProgressSection"
                    class="upload-progress-section"
                    *ngIf="
                        uploadProgress.isSending || uploadProgress.overallBytesTransferred > 0 || uploadProgress.error
                    "
                    #uploadProgressSection
                >
                    <!-- Phase Header -->
                    <div class="phase-header">
                        <span class="phase-indicator" [ngClass]="upgradePhase">
                            {{ upgradePhase === 'downloading' ? '📥 Downloading Firmware' : '📤 Uploading to Device' }}
                        </span>
                    </div>

                    <!-- Upload Progress -->
                    <div class="upload-progress">
                        <div class="progress-info">
                            <span class="progress-label">
                                {{ uploadProgress.currentFileName }}
                            </span>
                            <span class="progress-stats">
                                {{ uploadProgress.currentFileIndex + 1 }} / {{ uploadProgress.totalFiles }} files
                            </span>
                        </div>
                        <div class="progress-bar">
                            <div class="progress-fill" [style.width.%]="getProgressPercentage()"></div>
                        </div>
                        <div class="progress-details">
                            <span
                                >{{ formatBytes(uploadProgress.overallBytesTransferred) }} /
                                {{ formatBytes(uploadProgress.overallTotalBytes) }}</span
                            >
                            <span>{{ getProgressPercentage() }}%</span>
                        </div>
                    </div>

                    <!-- Upload Error -->
                    <div class="error-message" *ngIf="uploadProgress.error">
                        <strong>{{ upgradePhase === 'downloading' ? 'Download Error' : 'Upload Error' }}:</strong>
                        {{ uploadProgress.error }}
                    </div>

                    <!-- Upload Success -->
                    <div class="success-message" *ngIf="isUploadComplete() && upgradePhase === 'uploading'">
                        <strong>✓ Software package upload to TetraGraph completed:</strong> All files transferred
                        successfully to device
                    </div>

                    <!-- Verification Widget -->
                    <div class="verification-widget" *ngIf="isUploadComplete()">
                        <div class="verification-content">
                            <p
                                class="verification-text"
                                *ngIf="verificationStatus === 'idle' || verificationStatus === 'verifying'"
                            >
                                Restart your device by clicking on the Restart button on the TetraGraph. Wait for the
                                installation to complete, which takes approx. 2 minutes. When TetraGraph boots up, press
                                the button below to verify the installation.
                            </p>
                            <button
                                class="verify-btn"
                                (click)="onVerifyUpgrade()"
                                [disabled]="isVerifying || (clockSyncService.isSynchronizing$ | async)"
                                *ngIf="verificationStatus === 'idle' || verificationStatus === 'verifying'"
                            >
                                {{ isVerifying ? 'Verifying...' : 'Verify Installation' }}
                            </button>

                            <!-- Verification Progress -->
                            <div class="verification-progress" *ngIf="isVerifying">
                                <p>{{ verificationMessage }}</p>
                            </div>

                            <!-- Verification Success -->
                            <div class="success-message" *ngIf="verificationStatus === 'success'">
                                <strong>✓ Verification Successful:</strong> {{ verificationMessage }}
                            </div>

                            <!-- Verification Failed -->
                            <div class="error-message" *ngIf="verificationStatus === 'failed'">
                                <strong>✗ Verification Failed:</strong>
                                <p *ngIf="verificationError">{{ verificationError }}</p>
                                <p *ngIf="verificationMessage">{{ verificationMessage }}</p>
                                <p *ngIf="!verificationError && !verificationMessage">
                                    Unable to verify installation. Please try again.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
